import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { HttpError } from 'types';
import { UpsertParentOrganisationRequest } from '../../../api';

interface MessageProps {
  messageTitle: string;
  messageStatus: 'error' | 'success' | 'info';
}

export const useMessageProps = (
  status: 'error' | 'success' | 'idle' | 'loading',
  error: unknown,
  organisationId?: number,
  latestOrganisationRequest?: UpsertParentOrganisationRequest
): MessageProps => {
  const { t } = useTranslation('manage_organisation');
  const messagePropsMap = useMemo(() => {
    const memorizedMessageProps: Record<string, MessageProps> = {
      idle: {
        messageTitle:
          organisationId || latestOrganisationRequest?.parentOrganisationId
            ? ''
            : t(
                'info_message',
                "You must select 'Create organisation' before continuing to set up teams and users."
              ),
        messageStatus: 'info',
      },
      error: {
        messageTitle:
          organisationId || latestOrganisationRequest?.parentOrganisationId
            ? t(
                'update_error_message',
                'The organisation could not be updated. Please try again.'
              )
            : t('error_message', 'The organisation could not be created. Please try again.'),
        messageStatus: 'error',
      },
      duplicateName: {
        messageTitle: t(
          'duplicate_name_message',
          'The organisation name already exists. Please use another name to continue.'
        ),
        messageStatus: 'error',
      },
      success: {
        messageTitle: latestOrganisationRequest?.parentOrganisationId
          ? t('update_success_message', 'The organisation has been successfully updated.')
          : t('success_message', 'The organisation has been successfully created.'),
        messageStatus: 'success',
      },
    };

    return memorizedMessageProps;
  }, [t, organisationId, latestOrganisationRequest]);

  return useMemo(() => {
    const organisationNameExists = (): boolean => {
      const isExisting = (error as HttpError)?.errors?.OrganisationName?.find((x) =>
        x.includes('already exists.')
      );
      return !!isExisting;
    };
    if (status === 'loading') {
      return messagePropsMap.idle;
    } else if (organisationNameExists()) {
      return messagePropsMap.duplicateName;
    } else {
      return messagePropsMap[status];
    }
  }, [error, status, messagePropsMap]);
};
